import React from 'react';
import classNames from 'classnames/bind';
import RGL, { WidthProvider } from 'react-grid-layout';
import { DateTime } from 'luxon';
import { DatesRangeValue } from '@mantine/dates';
import { useToggle } from '@mantine/hooks';
import { useParams, useSearchParams } from 'react-router-dom';

import { DASHBOARD_COLUMNS } from 'types/dashboard';
import { DashboardParams } from 'dashboards/types/dashboard_params';
import { WidgetType } from 'types/dashboard_widget';
import { Gateway } from 'types/gateway';
import { RelativeDateRange } from 'components/DateRangePickerInput/time_range';

import {
  adjustTimezoneAndKeepLocalTime,
  useTimeRangeParams,
} from 'dashboards/hooks/useTimeRangeParams';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { useGateway } from 'gateways/hooks/useGateway';
import { useModal } from 'hooks/useModal';
import { useSpringState } from 'hooks/useSpringState';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { NotFound } from 'views/NotFound/NotFound';

import EmptyIllustration from 'dashboards/images/dashboard-empty-illustration.svg?react';
import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { DataWidget } from 'dashboards/widgets/Data/DataWidget';
import {
  DateRange,
  DateTimeRangeInput,
} from 'components/DateTimeRangeInput/DateTimeRangeInput';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { FilesSearchWidget } from 'dashboards/widgets/Files/FilesSearchWidget';
import { FilesWidget } from 'dashboards/widgets/Files/FilesWidget';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Icon } from 'components/Icon/Icon';
import { IconButton } from 'components/IconButton/IconButton';
import { IframeWidget } from 'dashboards/widgets/Iframe/IframeWidget';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { Loader } from 'components/Loader/Loader';
import { MultiStreamWidget } from 'dashboards/widgets/MultiStream/MultiStreamWidget';
import { StreamWidget } from 'dashboards/widgets/Stream/StreamWidget';
import { TagSelect } from 'tags/components/TagSelect/TagSelect';
import { TimezoneSelect } from 'components/TimezoneSelect/TimezoneSelect';

import styles from './DashboardView.module.scss';

const c = classNames.bind(styles);

const ReactGridLayout = WidthProvider(RGL);

const DASHBOARD_WIDGET_MAP: Record<WidgetType, any> = {
  data: DataWidget,
  iframe: IframeWidget,
  stream: StreamWidget,
  multistream: MultiStreamWidget,
  files: FilesWidget,
  'files-search': FilesSearchWidget,
};

export function DashboardView() {
  const { dashboardID: dashboardId } = useParams<DashboardParams>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { open } = useModal();

  const hideControls = searchParams.get('hideControls') === 'true';
  const numColumns =
    Number(searchParams.get('numColumns')) || DASHBOARD_COLUMNS;

  const { preset, start, stop, timezone } = useTimeRangeParams();
  const { gateway_id } = useDashboardFilterParams();
  const { data: selectedGateway, isInitialLoading: isLoadingSelectedGateway } =
    useGateway(gateway_id);

  const datePickerDefault: DateRange =
    preset === 'CUSTOM'
      ? [DateTime.fromISO(start).toJSDate(), DateTime.fromISO(stop).toJSDate()]
      : (preset as RelativeDateRange);
  const defaultTagsSelection = searchParams.get('tags')?.split(',');

  const gridRef = React.useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useToggle();

  const [didSave, setDidSave] = useSpringState(false, 2000);

  const [isEditingLayout, toggleIsEditingLayout] = useToggle();
  const [newLayout, setNewLayout] = React.useState<RGL.Layout[]>();

  const { mutate: updateDashboard, isLoading: isSaving } = useUpdateDashboard({
    onSuccess() {
      setDidSave(true);
      toggleIsEditingLayout(false);
    },
  });

  const { data: dashboard, isLoading, error } = useDashboard(dashboardId);

  function handleTimeRangeChange(dateRange?: DatesRangeValue, preset?: string) {
    if (!dateRange || !preset) {
      return;
    }

    const [from, to] = dateRange;

    if (!from || !to) {
      return;
    }

    const start = adjustTimezoneAndKeepLocalTime({
      timezone,
      date: DateTime.fromJSDate(from),
    });
    const stop = adjustTimezoneAndKeepLocalTime({
      timezone,
      date: DateTime.fromJSDate(to),
    });

    if (!start || !stop) {
      return;
    }

    setSearchParams((previous) => {
      previous.set('preset', preset);
      previous.set('start', start);
      previous.set('stop', stop);
      return previous;
    });
  }

  function handleTimezoneChange(timezone?: string) {
    if (!timezone || !start || !stop) {
      return;
    }

    // Replace timezone on start and stop dates by throwing away
    // the current timezone.
    const startNewTz = adjustTimezoneAndKeepLocalTime({
      timezone,
      date: DateTime.fromISO(start.slice(0, -6)),
    });
    const stopNewTz = adjustTimezoneAndKeepLocalTime({
      timezone,
      date: DateTime.fromISO(stop.slice(0, -6)),
    });

    if (!startNewTz || !stopNewTz) {
      return;
    }

    setSearchParams((previous) => {
      previous.set('start', startNewTz);
      previous.set('stop', stopNewTz);
      previous.set('timezone', timezone);
      return previous;
    });
  }

  function handleGatewayChange(newGateway: Gateway | null) {
    if (newGateway) {
      setSearchParams((previous) => {
        previous.set('gateway_id', newGateway.id);
        return previous;
      });
    } else {
      setSearchParams((previous) => {
        previous.delete('gateway_id');
        return previous;
      });
    }
  }

  function handleTagsChange(tags: string[]) {
    if (!tags.length) {
      setSearchParams((previous) => {
        previous.delete('tags');
        return previous;
      });
    } else {
      setSearchParams((previous) => {
        previous.set('tags', tags.join(','));
        return previous;
      });
    }
  }

  if (isLoading) {
    return <Loader text="Loading dashboard..." />;
  }

  if (error || !dashboard) {
    return <NotFound entity="dashboard" />;
  }

  function handleSaveLayout() {
    if (!dashboard || !newLayout) {
      return;
    }

    const updatedWidgets = dashboard.configuration.widgets.map((widget) => {
      const matchingLayout = newLayout.find((layout) => layout.i === widget.id);

      if (matchingLayout) {
        widget.layout = matchingLayout;
      }

      return widget;
    });

    updateDashboard({
      id: dashboard.id,
      configuration: { widgets: updatedWidgets },
    });
  }

  function toggleFullscreen() {
    if (!gridRef.current) {
      return;
    }

    if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
      return;
    }

    gridRef.current.requestFullscreen();
    setIsFullscreen(true);
  }

  const hasWidgets =
    dashboard.configuration.widgets &&
    dashboard.configuration.widgets.length > 0;

  const layout = !isEditingLayout
    ? dashboard.configuration.widgets.map(({ layout }) => layout)
    : undefined;

  return (
    <div className={c('wrap')} ref={gridRef}>
      {!hideControls && (
        <div className={c('controls')}>
          <div className={c('controls-primary')}>
            <DateTimeRangeInput
              className={c('controls-time-range')}
              defaultValue={datePickerDefault}
              onChange={handleTimeRangeChange}
              liveUpdate
            />
            <TimezoneSelect
              size="small"
              defaultValue={timezone}
              onChange={handleTimezoneChange}
            />
            <GatewaySelect
              size="small"
              defaultValue={selectedGateway}
              isLoading={isLoadingSelectedGateway}
              onChange={handleGatewayChange}
              isClearable
            />
            <TagSelect
              defaultValue={defaultTagsSelection}
              onChange={handleTagsChange}
              placeholder="Source labels"
            />
          </div>

          <ButtonGroup>
            {didSave && (
              <InlineNotification intent="success">Saved!</InlineNotification>
            )}

            <Button
              variant="secondary"
              size="small"
              onClick={() => dashboardId && open('add-widget', { dashboardId })}
            >
              <Icon name="plus" size="xsmall" />
              <span>Add widget</span>
            </Button>

            {isEditingLayout ? (
              <>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => toggleIsEditingLayout(false)}
                >
                  Discard changes
                </Button>
                <Button
                  size="small"
                  variant="primary"
                  onClick={handleSaveLayout}
                  loading={isSaving}
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="secondary"
                onClick={() => toggleIsEditingLayout()}
                disabled={dashboard.configuration.widgets.length === 0}
              >
                Edit layout
              </Button>
            )}

            <IconButton
              icon={isFullscreen ? 'collapse' : 'expand'}
              className={c('controls-toggle-fullscreen')}
              label="Toggle fullscreen"
              onClick={toggleFullscreen}
              size="small"
            />
          </ButtonGroup>
        </div>
      )}

      <div className={c('widgets')}>
        {!hasWidgets && (
          <EmptyView image={EmptyIllustration}>
            Select "Add widget" to get started.
          </EmptyView>
        )}

        {hasWidgets && (
          <ReactGridLayout
            layout={layout}
            className={c({ editable: isEditingLayout })}
            cols={numColumns}
            containerPadding={[0, 0]}
            isDraggable={isEditingLayout}
            isResizable={isEditingLayout}
            resizeHandles={isEditingLayout ? ['se'] : []}
            onLayoutChange={isEditingLayout ? setNewLayout : undefined}
          >
            {dashboard.configuration.widgets.map((widget) => {
              const Component = DASHBOARD_WIDGET_MAP[widget.type];

              if (!Component) {
                return null;
              }

              return (
                <div
                  className={c('grid-item')}
                  data-grid={widget.layout}
                  key={widget.id}
                >
                  <div className={c('widget')}>
                    <Component {...widget} />
                  </div>
                </div>
              );
            })}
          </ReactGridLayout>
        )}
      </div>
    </div>
  );
}
